"use client";

import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  value: string[];
  onValueChange: (value: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
  /** Id of the field's error text, referenced while `invalid` is set. */
  "aria-describedby"?: string;
  invalid?: boolean;
  required?: boolean;
  className?: string;
}

/**
 * Checkbox dropdown for a field that holds several values at once. Radix's
 * Select is single-value by design, so this is built on the menu primitive
 * while borrowing SelectTrigger's classes — the two sit side by side in the
 * invite dialog and would read as different controls otherwise.
 */
export function MultiSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select",
  disabled = false,
  id,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  invalid = false,
  required = false,
  className,
}: MultiSelectProps) {
  const summary = options
    .filter((option) => value.includes(option.value))
    .map((option) => option.label)
    .join(", ");

  const toggle = (optionValue: string) => {
    // Rebuilt from `options` rather than pushed onto `value`, so the summary
    // above always reads in option order regardless of click order.
    const next = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onValueChange(
      options.filter((o) => next.includes(o.value)).map((o) => o.value),
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        id={id}
        type="button"
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? ariaDescribedBy : undefined}
        aria-required={required || undefined}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
      >
        {/*
          The name is rendered as sr-only content rather than passed as
          `aria-label`, because `aria-label` REPLACES the element's content in
          the accessible name — the visible summary ("Sales, Marketing") would
          never be announced, and the current value would only be discoverable
          by opening the menu. `sr-only` is absolutely positioned, so it is out
          of flow and `justify-between` still distributes the other two children.
        */}
        {ariaLabel ? <span className="sr-only">{ariaLabel}</span> : null}
        <span className={cn("truncate", !summary && "text-muted-foreground")}>
          {summary || placeholder}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)]"
      >
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={value.includes(option.value)}
            // Kept open so several values can be picked in one visit.
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => toggle(option.value)}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
